import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import {
  DynamicPopup,
  DynamicPopupService,
} from 'src/app/services/dynamic-popup.service';

declare var $: any;

@Component({
  selector: 'app-dynamic-popup',
  templateUrl: './dynamic-popup.component.html',
  styleUrls: ['./dynamic-popup.component.css'],
})
export class DynamicPopupComponent implements OnInit {
  token: any;
  dynamicPopups: DynamicPopup[] = [];
  isLoading = false;

  // Pagination
  page = 1;
  limit = 10;
  total = 0;
  totalPages = 0;

  // Forms
  popupForm!: FormGroup;
  isEditMode = false;
  selectedFile: File | null = null;
  selectedPopup: DynamicPopup | null = null;
  submitted = false;
  removeImage = false;

  // Target Groups
  targetGroups = [
    'ALL',
    'MALES',
    'FEMALES',
    'PAID',
    'UNPAID',
    'SUBSCRIPTION_EXPIRED',
  ];

  constructor(
    private titleService: Title,
    private dynamicPopupService: DynamicPopupService,
    private toastr: ToastrService,
    private fb: FormBuilder
  ) {
    this.titleService.setTitle('Dynamic Popups');
  }

  ngOnInit(): void {
    this.token = localStorage.getItem('token');
    this.initForm();
    this.getAllDynamicPopups();
  }

  initForm() {
    this.popupForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      external_link: [''],
      active: [false, Validators.required],
      target_group: ['ALL', Validators.required],
      image: [null],
      repeat_frequency: [0, Validators.required],
    });
    this.updateDescriptionValidator();
  }

  get maxDescriptionLength(): number {
    const hasImage =
      !!this.selectedFile || (!!this.selectedPopup?.image && !this.removeImage);
    return hasImage ? 300 : 600;
  }

  updateDescriptionValidator() {
    const descriptionControl = this.popupForm.get('description');
    if (descriptionControl) {
      descriptionControl.setValidators([
        Validators.maxLength(this.maxDescriptionLength),
      ]);
      descriptionControl.updateValueAndValidity();
    }
  }

  get f() {
    return this.popupForm.controls;
  }

  getAllDynamicPopups() {
    this.isLoading = true;
    this.dynamicPopupService
      .getAllDynamicPopup(this.page, this.limit, this.token)
      .subscribe(
        (res: any) => {
          this.isLoading = false;
          if (res.status === 200) {
            if (res.data && res.data.popups) {
              this.dynamicPopups = res.data.popups;
              if (res.data.pagination) {
                this.total = res.data.pagination.total;
                this.totalPages = res.data.pagination.total_pages;
              }
            } else if (res.data && Array.isArray(res.data)) {
              this.dynamicPopups = res.data;
              this.total = res.total || res.data.length;
            } else {
              this.dynamicPopups = [];
            }
          } else {
            this.dynamicPopups = [];
          }
        },
        (err) => {
          this.isLoading = false;
          // this.toastr.error('Failed to fetch dynamic popups');
          console.error(err);
        }
      );
  }

  onPageChange(page: number) {
    this.page = page;
    this.getAllDynamicPopups();
  }

  onLimitChange(event: any) {
    this.limit = event.target.value;
    this.page = 1;
    this.getAllDynamicPopups();
  }

  openCreateModal() {
    this.isEditMode = false;
    this.selectedPopup = null;
    this.selectedFile = null;
    this.submitted = false;
    this.removeImage = false;
    this.popupForm.reset({
      active: false,
      repeat_frequency: 0,
      target_group: 'ALL',
      external_link: '',
    });
    this.updateDescriptionValidator();
    $('#createEditModal').modal('show');
  }

  openEditModal(popup: DynamicPopup) {
    this.isEditMode = true;
    this.selectedPopup = popup;
    this.selectedFile = null;
    this.submitted = false;
    this.removeImage = false;

    this.popupForm.patchValue({
      title: popup.title,
      description: popup.description,
      external_link: popup.external_link,
      active: popup.active,
      target_group: popup.target_group,
      repeat_frequency: popup.repeat_frequency,
    });

    this.updateDescriptionValidator();
    $('#createEditModal').modal('show');
  }

  openViewModal(popup: DynamicPopup) {
    this.selectedPopup = popup;
    $('#viewModal').modal('show');
  }

  openDeleteModal(popup: DynamicPopup) {
    this.selectedPopup = popup;
    $('#deleteModal').modal('show');
  }

  onFileChange(event: any) {
    if (event.target.files.length > 0) {
      this.selectedFile = event.target.files[0];
      this.removeImage = false; // Reset remove flag if new file selected
      this.updateDescriptionValidator();
    }
  }

  removeExistingImage() {
    this.removeImage = true;
    this.selectedFile = null;
    this.updateDescriptionValidator();
  }

  onSubmit() {
    this.submitted = true;
    if (this.popupForm.invalid) {
      return;
    }

    const formData = new FormData();
    formData.append('title', this.popupForm.get('title')?.value);
    formData.append(
      'description',
      this.popupForm.get('description')?.value || ''
    );
    const externalLink = this.popupForm.get('external_link')?.value;
    if (externalLink !== null && externalLink !== undefined) {
      formData.append('external_link', externalLink);
    }
    formData.append('active', this.popupForm.get('active')?.value);
    formData.append('target_group', this.popupForm.get('target_group')?.value);
    formData.append(
      'repeat_frequency',
      this.popupForm.get('repeat_frequency')?.value
    );

    if (this.selectedFile) {
      formData.append('image', this.selectedFile);
    }

    if (this.removeImage) {
      formData.append('remove_image', 'true');
    }

    this.isLoading = true;

    if (this.isEditMode && this.selectedPopup) {
      this.dynamicPopupService
        .updateDynamicPopup(this.selectedPopup.id, formData, this.token)
        .subscribe(
          (res: any) => {
            this.isLoading = false;
            if (res.status === 200 || res.status === 201) {
              this.toastr.success('Dynamic Popup updated successfully');
              $('#createEditModal').modal('hide');
              this.getAllDynamicPopups();
            } else {
              this.toastr.error(res.message || 'Failed to update');
            }
          },
          (err) => {
            this.isLoading = false;
            this.toastr.error('Something went wrong');
          }
        );
    } else {
      this.dynamicPopupService
        .createDynamicPopup(formData, this.token)
        .subscribe(
          (res: any) => {
            this.isLoading = false;
            if (res.status === 200 || res.status === 201) {
              this.toastr.success('Dynamic Popup created successfully');
              $('#createEditModal').modal('hide');
              this.getAllDynamicPopups();
            } else {
              this.toastr.error(res.message || 'Failed to create');
            }
          },
          (err) => {
            this.isLoading = false;
            this.toastr.error('Something went wrong');
          }
        );
    }
  }

  confirmDelete() {
    if (this.selectedPopup) {
      this.isLoading = true;
      this.dynamicPopupService
        .deleteDynamicPopup(this.selectedPopup.id, this.token)
        .subscribe(
          (res: any) => {
            this.isLoading = false;
            if (res.status === 200) {
              this.toastr.success('Dynamic Popup deleted successfully');
              $('#deleteModal').modal('hide');
              this.getAllDynamicPopups();
            } else {
              this.toastr.error(res.message || 'Failed to delete');
            }
          },
          (err) => {
            this.isLoading = false;
            this.toastr.error('Something went wrong');
          }
        );
    }
  }
}
